import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";

import { AiAssistButton, AiDraftNotice, useAiAssist } from "@/components/communications/AiAssist";
import { MESSAGE_BODY_MAX, MESSAGE_SUBJECT_MAX } from "@/components/communications/threadFormat";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";

// One labelled row of the composer header, the way a mail client stacks To
// and Subject: the label inline, the field borderless, a rule underneath.
const HeaderRow = ({ id, label, children }) => (
  <div className="flex items-center gap-3 border-b border-border-primary px-5">
    <label htmlFor={id} className="w-14 shrink-0 text-sm text-text-secondary">
      {label}
    </label>
    {children}
  </div>
);

const bare = "rounded-none border-0 bg-transparent px-0 focus-visible:ring-0";

/**
 * A new email — the Compose window of a mail client. To is the candidate's
 * address and is not editable: this tab writes to the person on screen and
 * nobody else. The AI writes from what the recruiter asks for, or improves
 * what they wrote, into these same fields; they edit or undo from there, and
 * nothing is sent that they have not read (§1.3).
 *
 * There is no attachment control anywhere in here, by decision (§1.4).
 */
const ComposeModal = ({ open, to, onClose, onSend, onDraft }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const value = { subject, body };
  const apply = useCallback((next) => {
    setSubject(next.subject || "");
    setBody(next.body || "");
  }, []);
  const assist = useAiAssist({ value, onChange: apply, request: onDraft });
  const { reset } = assist;

  useEffect(() => {
    if (open) {
      setSubject("");
      setBody("");
      setSending(false);
      reset();
    }
  }, [open, reset]);

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend({ subject: subject.trim(), body: body.trim() });
    } finally {
      setSending(false);
    }
  };

  const busy = sending || assist.running;
  const canSend = !busy && subject.trim().length > 0 && body.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="max-w-2xl gap-0 p-0">
        <DialogHeader className="px-5 py-3">
          <DialogTitle className="text-base">New email</DialogTitle>
          <DialogDescription className="sr-only">Starts a new conversation about this application.</DialogDescription>
        </DialogHeader>

        <HeaderRow id="compose-to" label="To">
          <Input id="compose-to" value={to || ""} readOnly className={bare} />
        </HeaderRow>
        <HeaderRow id="compose-subject" label="Subject">
          <Input id="compose-subject" value={subject} maxLength={MESSAGE_SUBJECT_MAX} disabled={assist.running} onChange={(e) => setSubject(e.target.value)} className={bare} />
        </HeaderRow>

        <label htmlFor="compose-body" className="sr-only">
          Message
        </label>
        <Textarea
          id="compose-body"
          value={body}
          maxLength={MESSAGE_BODY_MAX}
          rows={12}
          disabled={assist.running}
          onChange={(e) => setBody(e.target.value)}
          placeholder={assist.running ? "Writing…" : "Write your email, or ask the AI to write it"}
          className={`${bare} min-h-64 resize-y px-5 py-4`}
        />

        <AiDraftNotice assist={assist} className="mx-5 mb-3" />

        <div className="flex items-center justify-between gap-2 border-t border-border-primary px-5 py-3">
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" disabled={!canSend} onClick={handleSend}>
              {sending ? "Sending…" : "Send"}
            </Button>
            <AiAssistButton assist={assist} disabled={sending} />
          </div>
          <Button type="button" variant="ghost" size="icon-sm" className="rounded-lg" aria-label="Discard email" disabled={busy} onClick={onClose}>
            <Trash2 aria-hidden="true" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeModal;
